import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import RichTextEditor from '@/components/rich-text-editor';
import { RichTextEditorRef } from '@/components/rich-text-editor/rich-text-editor';
import {
	RTE_EXTENSIONS,
	VariableItem,
	VariableProps,
} from '@/components/rich-text-editor/types/rich-text-editor';

import { COPY } from '@/shared/constants/copies';
import { BUILT_IN_MESSAGE_VARIABLES } from '@/shared/constants/message-constants';

import { cn } from '@/lib/utils';

export interface ChatMessageInputProps {
	value: string;
	onChange: (value: string) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	rteClassName?: string;
	contentClassName?: string;
	// Variable list props
	enableVariableList?: boolean;
	variables?: VariableItem[];
	getVariableTooltip?: (variable: VariableItem) => string;
	variableTriggerChar?: string;
	/** When false, variable pills render without curly-brace brackets. Defaults to true. */
	variableShowBrackets?: boolean;
	/** Render variables as inline `@mention` text (blue, no background) instead of a filled pill. */
	variableMentionStyle?: boolean;
	/** Async source for the variable dropdown (e.g. server search). Debounced, with a
	 *  loading skeleton, results shown alongside the static `variables`. */
	variableSearchItems?: (query: string) => Promise<VariableItem[]>;
	/** Min query length before `variableSearchItems` fires. 0 = fire on the bare trigger. */
	variableSearchMinChars?: number;
	/** Debounce before `variableSearchItems` fires, in ms. */
	variableSearchDebounceMs?: number;
	/** Copy shown when a resolved search returns nothing. */
	variableSearchEmptyText?: string;
	/** Section label pinned above the async results (e.g. "Forms"). */
	variableSearchHeader?: string;
		/** Additional independently configured variable triggers. */
	additionalVariableProps?: VariableProps[];
	/** Enforce a single line: block Enter (except to pick a variable) and flatten pasted newlines. */
	singleLine?: boolean;
}

export interface ChatMessageInputRef {
	focus: () => void;
	blur: () => void;
	clearContent: () => void;
	setContent: (content: string) => void;
	addVariable: (variable: { id: string; token: string; label: string; prefix?: string }) => void;
	removeVariable: (id: string) => void;
}

const ChatMessageInput = forwardRef<ChatMessageInputRef, ChatMessageInputProps>(
	(
		{
			value,
			onChange,
			onKeyDown,
			onFocus,
			onBlur,
			rteClassName,
			placeholder = 'Type your message...',
			disabled = false,
			className,
			contentClassName,
			enableVariableList = false,
			variables,
			getVariableTooltip,
			variableTriggerChar,
			variableShowBrackets = true,
			variableMentionStyle = false,
			variableSearchItems,
			variableSearchMinChars,
			variableSearchDebounceMs,
			variableSearchEmptyText,
			variableSearchHeader,
				additionalVariableProps,
			singleLine = false,
		},
		ref
	) => {
		const editorRef = useRef<RichTextEditorRef>(null);

		// Forward ref methods to parent
		useImperativeHandle(
			ref,
			() => ({
				focus: () => editorRef.current?.focus(),
				blur: () => editorRef.current?.blur(),
				clearContent: () => editorRef.current?.clearContent(),
				setContent: (content: string) => editorRef.current?.setContent(content),
				addVariable: (variable) => editorRef.current?.addVariable(variable),
				removeVariable: (id: string) => editorRef.current?.removeVariable(id),
			}),
			[]
		);

		// Handle content changes from RTE
		const handleChange = useCallback(
			(newValue: string) => {
				onChange(newValue);
			},
			[onChange]
		);

		useEffect(() => {
			editorRef?.current?.setContent(value);
		}, [value]);

		const variableItems = useMemo(() => variables ?? BUILT_IN_MESSAGE_VARIABLES, [variables]);

		const variableProps = useMemo(
			() => ({
				items: enableVariableList ? variableItems : [],
				getTooltip: (item: VariableItem) =>
					getVariableTooltip?.(item) ||
					(item?.sampleValue
						? COPY.messaging.variableReplacedWith(item.sampleValue)
						: COPY.messaging.variableReplaced),
				triggerChar: variableTriggerChar,
				showBrackets: variableShowBrackets,
				mentionStyle: variableMentionStyle,
				mentionPrefix: variableMentionStyle ? variableTriggerChar ?? '@' : undefined,
				searchItems: variableSearchItems,
				searchMinChars: variableSearchMinChars,
				searchDebounceMs: variableSearchDebounceMs,
				searchEmptyText: variableSearchEmptyText,
				searchHeader: variableSearchHeader,
			}),
			[
				enableVariableList,
				variableItems,
				getVariableTooltip,
				variableTriggerChar,
				variableShowBrackets,
				variableMentionStyle,
				variableSearchItems,
				variableSearchMinChars,
				variableSearchDebounceMs,
				variableSearchEmptyText,
				variableSearchHeader,
			]
		);

		const variableTriggerProps = useMemo(
			() => additionalVariableProps ?? [],
			[additionalVariableProps]
		);

		return (
			<div className={cn('relative', className)}>
				<RichTextEditor
					ref={editorRef}
					description={value}
					onChangeDescription={handleChange}
					editMode={!disabled}
					allowedExtensions={[
						RTE_EXTENSIONS.STARTER_KIT,
						RTE_EXTENSIONS.PLACEHOLDER,
						RTE_EXTENSIONS.VARIABLE,
					]}
					placeholder={placeholder}
					onFocus={onFocus}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					variableProps={variableProps}
					additionalVariableProps={variableTriggerProps}
					singleLine={singleLine}
					hideToolbar
					hideFocusUnderline
					stopOnChangeDebounce
					className={cn(
						'max-h-[200px] min-h-[30px] overflow-y-auto',
						'rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-within:border-primary focus-within:outline-none focus-within:ring-1 focus-within:ring-primary disabled:cursor-not-allowed disabled:opacity-50',
						rteClassName
					)}
					contentClassName={cn('text-sm min-h-[30px] leading-normal', contentClassName)}
				/>
			</div>
		);
	}
);

ChatMessageInput.displayName = 'ChatMessageInput';

export default ChatMessageInput;
